import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, ShieldCheck, FileText } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { BUSINESS } from "../lib/content";

const sections = [
  {
    id: "intro",
    title: "1. Introduction",
    body: (
      <>
        <p>
          North West Asphalt ("we", "us", "our") respects your privacy and is
          committed to protecting any personal information you share with us.
          This Privacy Policy explains what information we collect, how we use
          it, and the choices you have when you interact with our website,
          phone line, or paving services.
        </p>
        <p>
          By using our website or contacting us, you agree to the practices
          described in this policy. If you do not agree, please do not use the
          website or share information with us.
        </p>
      </>
    ),
  },
  {
    id: "info-we-collect",
    title: "2. Information We Collect",
    body: (
      <>
        <p>
          <strong className="text-[#fafaf9]">Information you provide:</strong>{" "}
          When you call, message, or request a quote, we may collect your name,
          phone number, email address, service address and project details.
          You provide this information voluntarily.
        </p>
        <p>
          <strong className="text-[#fafaf9]">Automatic information:</strong>{" "}
          When you visit this website, our hosting provider may automatically
          log standard information such as your IP address, browser type,
          device type, referring URL and pages viewed. This information is
          used for security and basic analytics only.
        </p>
        <p>
          <strong className="text-[#fafaf9]">Advertising pixels:</strong>{" "}
          If we run paid campaigns (for example on Meta / Facebook / Instagram
          or Google), our website may include advertising pixels or tags that
          measure ad effectiveness, attribute conversions, and serve relevant
          ads. These pixels collect limited browser and event data in
          accordance with the platform's own privacy policies.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li>Respond to quotes, inquiries and service requests.</li>
        <li>Schedule and deliver our paving and maintenance services.</li>
        <li>Send appointment confirmations and follow-up messages.</li>
        <li>Improve our website experience and service quality.</li>
        <li>
          Measure the effectiveness of our advertising and remarketing
          campaigns.
        </li>
        <li>Comply with legal obligations and resolve disputes.</li>
      </ul>
    ),
  },
  {
    id: "sharing",
    title: "4. How We Share Information",
    body: (
      <>
        <p>
          We do not sell or rent your personal information. We may share
          limited information with:
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            <strong className="text-[#fafaf9]">Service providers</strong> — for
            hosting, email delivery, customer relationship management and
            advertising platforms (e.g. Meta, Google).
          </li>
          <li>
            <strong className="text-[#fafaf9]">Legal authorities</strong> —
            when required by law, court order, or to protect our rights and
            safety.
          </li>
          <li>
            <strong className="text-[#fafaf9]">Business transfers</strong> — in
            connection with a merger, acquisition, or sale of assets, subject
            to standard confidentiality protections.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "5. Cookies & Tracking Technologies",
    body: (
      <>
        <p>
          Our website may use cookies and similar technologies (pixels, local
          storage) for essential site function, basic analytics, and
          advertising measurement.
        </p>
        <p>
          You can disable or delete cookies through your browser settings.
          Doing so may limit some features of the website but will not stop
          you from using our paving services.
        </p>
      </>
    ),
  },
  {
    id: "meta-google",
    title: "6. Meta (Facebook) & Google Advertising",
    body: (
      <>
        <p>
          We may use Meta Pixel, Conversions API, and Google Ads tags to
          measure ad performance and reach people who have already shown
          interest in our services. These tools may collect identifiers such
          as browser data, IP address, and engagement events (e.g. page views,
          quote requests).
        </p>
        <p>
          To learn more or opt out of personalized advertising:
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Meta ad preferences:{" "}
            <a
              href="https://www.facebook.com/adpreferences"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#f59e0b] hover:underline"
            >
              facebook.com/adpreferences
            </a>
          </li>
          <li>
            Google ad settings:{" "}
            <a
              href="https://adssettings.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#f59e0b] hover:underline"
            >
              adssettings.google.com
            </a>
          </li>
          <li>
            Digital Advertising Alliance of Canada opt-out:{" "}
            <a
              href="https://youradchoices.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#f59e0b] hover:underline"
            >
              youradchoices.ca
            </a>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "7. Data Retention",
    body: (
      <p>
        We keep your personal information only as long as needed to provide
        services, respond to inquiries, and meet legal or accounting
        requirements. After that, we securely delete or anonymise it.
      </p>
    ),
  },
  {
    id: "security",
    title: "8. Security",
    body: (
      <p>
        We use reasonable physical, technical and administrative safeguards to
        protect personal information from unauthorised access, loss or misuse.
        No method of transmission over the internet is 100% secure, but we
        take privacy seriously and act promptly if any incident occurs.
      </p>
    ),
  },
  {
    id: "rights",
    title: "9. Your Rights",
    body: (
      <>
        <p>
          Depending on your province or country, you may have the right to:
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>Access the personal information we hold about you.</li>
          <li>Request a correction if your information is inaccurate.</li>
          <li>Request deletion of your personal information.</li>
          <li>Withdraw consent for marketing communications.</li>
          <li>Object to certain types of processing.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us using the details below.
          We will respond within a reasonable timeframe.
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "10. Third-Party Links",
    body: (
      <p>
        Our website may include links to third-party sites or platforms (for
        example, social media). We are not responsible for the privacy
        practices or content of those sites. Please review their privacy
        policies before sharing information with them.
      </p>
    ),
  },
  {
    id: "children",
    title: "11. Children's Privacy",
    body: (
      <p>
        Our services are intended for property owners and businesses. We do
        not knowingly collect personal information from children under 13. If
        you believe a child has provided us with personal information, please
        contact us so we can delete it.
      </p>
    ),
  },
  {
    id: "changes",
    title: "12. Changes to This Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. The "Last
        updated" date at the top will reflect the most recent revision.
        Continued use of our website after changes are posted means you accept
        the updated policy.
      </p>
    ),
  },
  {
    id: "contact",
    title: "13. Contact Us",
    body: (
      <>
        <p>
          For privacy questions or to exercise your rights, please contact:
        </p>
        <div className="border border-[#27272a] bg-[#18181b] p-5 mt-3 space-y-2">
          <div className="font-bold text-[#fafaf9]">
            North West Asphalt — Privacy Officer
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#f59e0b]" />
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="hover:text-[#f59e0b] transition-colors"
            >
              {BUSINESS.phone}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#f59e0b]" />
            <a
              href={`tel:${BUSINESS.phone2Raw}`}
              className="hover:text-[#f59e0b] transition-colors"
            >
              {BUSINESS.phone2}
            </a>
          </div>
        </div>
      </>
    ),
  },
];

export default function Privacy() {
  const updated = "1 January 2026";

  return (
    <div data-testid="privacy-page">
      <PageHero
        eyebrow="Legal"
        title="Privacy"
        accent="Policy."
        subtitle="How North West Asphalt collects, uses and protects your information."
        testId="privacy-hero"
      />

      {/* Meta band */}
      <section
        className="bg-[#050505] border-b border-[#27272a] py-8"
        data-testid="privacy-meta"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
            <ShieldCheck className="w-4 h-4 text-[#f59e0b]" />
            <span>Last updated: {updated}</span>
          </div>
          <Link
            to="/contact"
            data-testid="privacy-contact-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#fafaf9] hover:text-[#f59e0b] transition-colors"
          >
            <FileText className="w-4 h-4" />
            Privacy questions? Contact us
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 md:py-28" data-testid="privacy-body">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-32">
              <div className="label-sm text-[#f59e0b] mb-4">Contents</div>
              <ul className="space-y-2.5">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-xs text-[#a1a1aa] hover:text-[#f59e0b] transition-colors block leading-snug"
                      data-testid={`privacy-toc-${s.id}`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-12">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                className="scroll-mt-28"
                data-testid={`privacy-section-${s.id}`}
              >
                <h2 className="font-display font-black text-2xl md:text-3xl uppercase mb-5 leading-tight">
                  {s.title}
                </h2>
                <div className="space-y-4 text-[#d4d4d8] text-base leading-relaxed">
                  {s.body}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
